import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';
import { Table } from 'reactstrap';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import logger from 'src/util/logger';

import AdminButtonWithStateToolbar from '../AdminPortfolioControls/AdminButtonWithStateToolbar';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_portfolio.admin_portfolio_list.pagination_error.message',
    defaultMessage: 'Failed to load more portfolios. Please try again.',
  },
});

const AdminPortfolioListFragment = graphql`
fragment AdminPortfolioList_viewer on Viewer
@throwOnFieldError
@refetchable(queryName: "AdminPortfolioListRefetchQuery")
@argumentDefinitions(
  cursor: { type: "String" }
  count: { type: "Int" }
) {
  id
  portfolios(first: $count, after: $cursor)
    @connection(key: "AdminPortfolioList_portfolios") {
    edges {
      cursor
      node {
        uuid
        externalIdentifier
        title
        active {
          start
          finish
        }
      }
    }
  }
}
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={4} rowCount={3} />;
}

/**
 * Admin list of portfolios with load-more pagination. Field errors throw to the
 * route's PlaceholderErrorBoundary; load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactComponentElement} AdminPortfolioList component.
 */
function AdminPortfolioList({ viewer: viewerRef = null }) {
  const {
    data, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminPortfolioListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);
  const intl = useIntl();

  /**
   * Loads the next page of portfolios, keeping a failure for the retry modal.
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  if (!data) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminPortfolioList: the route query returned no viewer');
  }

  const portfolioList = data.portfolios?.edges?.map((edge) => edge.node) || [];

  const pageTitle = intl.formatMessage({
    id: 'admin.admin_portfolio.admin_portfolio_list.page.portfolios.title', defaultMessage: 'Enosi Admin - Properties',
  });
  const breadcrumbsAdmin = intl.formatMessage({
    id: 'admin.admin_portfolio.admin_portfolio_list.page.breadcrumbs.admin.label', defaultMessage: 'Admin',
  });
  const portfoliosLabel = intl.formatMessage({
    id: 'admin.admin_portfolio.admin_portfolio_list.page.portfolios.label', defaultMessage: 'Portfolios',
  });
  const addportfolioLabel = intl.formatMessage({
    id: 'admin.admin_portfolio.admin_portfolio_list.page.portfolio_create.label', defaultMessage: 'Add portfolio',
  });
  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_list.load_more_button.state_no_more.label', defaultMessage: 'No more portfolios to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsAdmin, path: '/admin' },
          { name: portfoliosLabel },
        ]}
      />
      <div className="clearfix">
        <h1 className="float-start">{portfoliosLabel}</h1>
        <div className="float-end">
          <AdminButtonWithStateToolbar
            isActive
            label={addportfolioLabel}
            target="/admin/portfolios/new"
            className="btn btn-darken"
          />
        </div>
      </div>
      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <Table className="table">
            <thead>
              <tr>
                <th scope="col">
                  {intl.formatMessage({
                    id: 'admin.admin_portfolio.admin_portfolio_list.table.head.status.label', defaultMessage: 'Status',
                  })}

                </th>
                <th scope="col">
                  {intl.formatMessage({
                    id: 'admin.admin_portfolio.admin_portfolio_list.table.head.title.label', defaultMessage: 'Title',
                  })}
                </th>
                <th scope="col">
                  {intl.formatMessage({
                    id: 'admin.admin_portfolio.admin_portfolio_list.table.head.id.label', defaultMessage: 'ID',
                  })}
                </th>
                <th scope="col">
                  <abbr title="External identifier">
                    {intl.formatMessage({
                      id: 'admin.admin_portfolio.admin_portfolio_list.table.external_identifier.label', defaultMessage: 'Ext ID',
                    })}
                  </abbr>
                </th>
              </tr>
            </thead>
            <tbody>
              {
                portfolioList.map((portfolio) => {
                  const {
                    active, externalIdentifier, uuid, title,
                  } = portfolio || {};

                  return (
                    <tr key={`admin-property-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row">
                        <Link to={`/admin/portfolios/${uuid}`}>
                          {title}
                        </Link>
                      </th>
                      <td>
                        <UUID uuid={uuid} />
                      </td>
                      <td>
                        {externalIdentifier}
                      </td>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </Table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminPortfolioList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminPortfolioList;
